import { PerformanceReviewerStrategiesStrategyEnum } from 'generated/resources/datatable.graphql'
import {
  PerformanceAnswersWithSectionsFragment,
  PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment,
  PerformanceQuestionSingleLabelInput,
  PerformanceQuestionnaireByReviewerStrategyFragment,
  PerformanceTemplateQuestionnaireByReviewerStrategyFragment,
  QuestionnaireContentFragment,
} from 'generated/resources/graphql'
import { sumBy } from 'lodash'

import i18n from 'modules/core/lib/i18n'

import type {
  AnswerType,
  Question,
  QuestionResponse,
  QuestionnaireResponseContent,
  Translations as QuestionnaireTranslations,
} from 'modules/questionnaires/lib/types'

type PerformanceQuestionSingleLabel = QuestionnaireContentFragment['questions'][number]
type PerformanceAnswer =
  PerformanceAnswersWithSectionsFragment['content'][number]['questions'][number]['answer']

export type QuestionnaireContent = QuestionnaireContentFragment & { locked?: boolean }
export type QuestionnaireByReviewerStrategy =
  | PerformanceQuestionnaireByReviewerStrategyFragment
  | PerformanceCustomTemplateQuestionnaireByReviewerStrategyFragment

type QuestionnairesFragments =
  | QuestionnaireByReviewerStrategy
  | PerformanceTemplateQuestionnaireByReviewerStrategyFragment

type StrategiesGeneralQuestionnaires = Pick<
  QuestionnairesFragments,
  'selfQuestionnaire' | 'managerQuestionnaire' | 'directReportQuestionnaire' | 'peersQuestionnaire'
>
type ScoreQuestionnaires = Pick<
  QuestionnairesFragments,
  'employeeScoreSelfQuestionnaire' | 'employeeScoreManagerQuestionnaire'
>

type PotentialScoreQuestionnaires = Pick<
  QuestionnairesFragments,
  'employeePotentialScoreManagerQuestionnaire'
>

type CompetenciesPlaceholderQuestionnaires = Pick<
  QuestionnairesFragments,
  'competenciesPlaceholderSelfQuestionnaire' | 'competenciesPlaceholderManagerQuestionnaire'
>

const withLockedContent = (content: QuestionnaireContentFragment): QuestionnaireContent => ({
  ...content,
  locked: true,
})

export const countQuestions = (contents: QuestionnaireContent[]): number =>
  sumBy(contents, (content) => content.questions.length)

export const questionsForStrategy = (
  questionnaires: StrategiesGeneralQuestionnaires | undefined,
  strategy: PerformanceReviewerStrategiesStrategyEnum
): QuestionnaireContent[] => {
  switch (strategy) {
    case 'self':
      return questionnaires?.selfQuestionnaire?.content ?? []
    case 'manager':
      return questionnaires?.managerQuestionnaire?.content ?? []
    case 'direct_reports':
      return questionnaires?.directReportQuestionnaire?.content ?? []
    case 'peers':
      return questionnaires?.peersQuestionnaire?.content ?? []
    default:
      return []
  }
}

export const employeeScoreForStrategy = (
  questionnaires: ScoreQuestionnaires | undefined,
  strategy: PerformanceReviewerStrategiesStrategyEnum
): QuestionnaireContent[] => {
  switch (strategy) {
    case 'self':
      return questionnaires?.employeeScoreSelfQuestionnaire?.content?.map(withLockedContent) ?? []
    case 'manager':
      return (
        questionnaires?.employeeScoreManagerQuestionnaire?.content?.map(withLockedContent) ?? []
      )
    default:
      return []
  }
}

export const employeePotentialScoreForStrategy = (
  questionnaires: PotentialScoreQuestionnaires | undefined,
  strategy: PerformanceReviewerStrategiesStrategyEnum
): QuestionnaireContent[] => {
  switch (strategy) {
    case 'manager':
      return (
        questionnaires?.employeePotentialScoreManagerQuestionnaire?.content?.map(
          withLockedContent
        ) ?? []
      )
    default:
      return []
  }
}

export const competenciesPlaceholderForStrategy = (
  questionnaires: CompetenciesPlaceholderQuestionnaires | undefined,
  strategy: PerformanceReviewerStrategiesStrategyEnum
): QuestionnaireContent[] => {
  switch (strategy) {
    case 'self':
      return (
        questionnaires?.competenciesPlaceholderSelfQuestionnaire?.content?.map(withLockedContent) ??
        []
      )
    case 'manager':
      return (
        questionnaires?.competenciesPlaceholderManagerQuestionnaire?.content?.map(
          withLockedContent
        ) ?? []
      )
    default:
      return []
  }
}

export const getQuestionnaireTranslations = (): QuestionnaireTranslations => {
  return {
    questionnaire: {
      add_question: i18n.t('performance_process.reviews.questionnaire_add_question'),
      add_section: i18n.t('performance_process.reviews.questionnaires_by_strategy.add_section'),
      empty_state: {
        title: i18n.t('questionnaires.questionnaire_response.empty_state.message'),
        description: '',
      },
    },
    section: {
      questions_count: ({ count }) =>
        i18n.tp('performance_process.reviews.questionnaires_by_strategy.section.questions_count', {
          count,
        }),
      questions: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.questions'),
      question: i18n.t('performance_process.reviews.questionnaires_by_strategy.question'),
      add_question: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.section.add_question'
      ),
      title: {
        placeholder: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.title_placeholder'
        ),
        save: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.title_save'),
        copy: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.title_copy'),
      },
      toggle: i18n.t('performance_process.reviews.questionnaires_by_strategy.section.toggle'),
      default_actions: {
        edit: i18n.t('performance_process.reviews.questionnaires_by_strategy.default_actions.edit'),
        duplicate: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.duplicate'
        ),
        delete: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.delete'
        ),
      },
      delete_modal: {
        title: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.title'
        ),
        description: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.description'
        ),
        confirm: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.confirm'
        ),
        cancel: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.section.delete_modal.cancel'
        ),
      },
    },
    question: {
      mandatory: i18n.t('performance_process.reviews.mandatory'),
      copy_placeholder: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.questions.copy_placeholder'
      ),
      label: i18n.t('performance_process.reviews.questionnaires_by_strategy.question'),
      placeholder: i18n.t(
        'performance_process.reviews.questionnaires_by_strategy.questions.placeholder'
      ),
      description: {
        label: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.question_description.label'
        ),
        placeholder: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.question_description.placeholder'
        ),
      },
      optional_comment: {
        label: i18n.t('performance_process.reviews.participants_can_comment'),
        information: i18n.t('performance_process.reviews.comment_required_info'),
      },
      multiple_choice_configurator: {
        add_option: i18n.t('performance_process.reviews.question_types.add_option'),
        placeholder: i18n.t('performance_process.reviews.question_types.add_option_placeholder'),
      },
      types: {
        number: i18n.t('performance_process.reviews.question_types.number'),
        multiple_choice: i18n.t('performance_process.reviews.question_types.multiple_choice'),
        text: i18n.t('performance_process.reviews.question_types.text'),
        rating: i18n.t('performance_process.reviews.question_types.rating'),
        single_choice: i18n.t('performance_process.reviews.question_types.single_choice'),
      },
      type_selector: {
        label: i18n.t('performance_process.reviews.question_types.type_of_answer'),
        placeholder: i18n.t('performance_process.reviews.question_types.select_type'),
        blank_label: i18n.t('performance_process.reviews.question_types.blank'),
      },
      actions: {
        edit: i18n.t('performance_process.reviews.questionnaires_by_strategy.default_actions.edit'),
        duplicate: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.duplicate'
        ),
        delete: i18n.t(
          'performance_process.reviews.questionnaires_by_strategy.default_actions.delete'
        ),
      },
      save: i18n.t('performance_process.reviews.questionnaires_by_strategy.questions.save'),
      validations: {
        title: {
          required: i18n.t('performance_process.reviews.question_types.errors.title_required'),
        },
        answer_type: {
          choice_options: {
            min_two_options: i18n.t(
              'performance_process.reviews.question_types.errors.min_two_options'
            ),
          },
        },
        options: {
          required: i18n.t(
            'performance_process.reviews.question_types.errors.required_choice_option'
          ),
          repeated: i18n.t(
            'performance_process.reviews.question_types.errors.repeated_choice_option'
          ),
        },
      },
    },
  }
}

export const mapQuestion: (
  q: PerformanceQuestionSingleLabel | PerformanceQuestionSingleLabelInput
) => Question = (q) => ({
  uuid: q.uuid,
  title: q.title,
  description: q.description,
  scale: q?.scale?.map((s) => ({ value: s.value, text: s.text })),
  answerType: q.answerType,
  maxChoices: q.maxChoices || null,
  choiceOptions: q.choiceOptions || [],
  mandatory: q.mandatory,
  withComment: q.withComment,
})
const mapAnswer = (answer: PerformanceAnswer | undefined | null): AnswerType | null => {
  if (!answer) return null
  return {
    uuid: answer.uuid,
    answerChoice: answer.answerChoice ?? [],
    answerRating: answer.answerRating
      ? {
          value: answer.answerRating?.value ?? undefined,
          comment: answer.answerRating?.comment ?? undefined,
        }
      : null,
    answerText: answer.answerText ?? undefined,
    answerInt: answer.answerInt ?? undefined,
  }
}

const mapQuestionResponse = (
  question: PerformanceQuestionSingleLabel,
  answer: PerformanceAnswer | null | undefined
): QuestionResponse => {
  return {
    question: mapQuestion(question),
    answer: mapAnswer(answer),
  }
}

export const mapQuestionnaireResponse = (
  answeredQuestionnaireWithSections?: PerformanceAnswersWithSectionsFragment | null
): QuestionnaireResponseContent => {
  if (!answeredQuestionnaireWithSections) return []

  const content = answeredQuestionnaireWithSections?.content || []
  return content
    .map((item) => {
      if (item.type === 'answered_question') {
        const questionResponse = item.questions[0]
        if (!questionResponse) return

        return mapQuestionResponse(questionResponse.question, questionResponse.answer)
      }

      if (item.type === 'answered_section') {
        return {
          title: item.sectionTitle,
          uuid: item.uuid,
          questions: item.questions
            .map((questionResponse) => {
              return mapQuestionResponse(questionResponse.question, questionResponse.answer)
            })
            .filter(Boolean),
        }
      }
    })
    .filter(Boolean)
}
